/** Times are stored in UTC and displayed in Africa/Lusaka (CLAUDE.md conventions). */
export const LUSAKA_TZ = "Africa/Lusaka";

// The Hub's date style (ADR 0020): "Mon 28 Sep, 10:00"; the year only when it is not this year ("Wed 31 Dec 2025, 23:59").
// Names are fixed here: ICU data differs between engines ("Sep" or "Sept").
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dateTimeParts = new Intl.DateTimeFormat("en-GB", {
  timeZone: LUSAKA_TZ,
  day: "numeric",
  month: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

function lusakaParts(d: Date): Record<string, string> {
  const p: Record<string, string> = {};
  for (const x of dateTimeParts.formatToParts(d)) p[x.type] = x.value;
  return p;
}

function hubDateTime(d: Date, thisYear: string): string {
  const p = lusakaParts(d);
  const weekday = WEEKDAYS[new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day))).getUTCDay()];
  const year = p.year === thisYear ? "" : ` ${p.year}`;
  return `${weekday} ${Number(p.day)} ${MONTHS[Number(p.month) - 1]}${year}, ${p.hour}:${p.minute}`;
}

// Date-only values (certificate validity, report windows) are calendar dates, not instants: format them without shifting.
const isoDayFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: LUSAKA_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** "Sun 27 Sep, 14:05" in Lusaka time (with the year when it is not this year), from an RFC 3339 UTC timestamp. */
export function formatDateTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const thisYear = lusakaParts(now).year ?? "";
  return hubDateTime(d, thisYear);
}

/**
 * "27 Sep 2026" from a YYYY-MM-DD date. Given an RFC 3339 timestamp instead, its Lusaka day (never the UTC day cut from
 * the string, review RW3).
 */
export function formatDate(day: string | null | undefined): string {
  if (!day) return "—";
  if (day.length > 10) {
    const lusakaDay = lusakaDayOf(day);
    return lusakaDay ? formatDate(lusakaDay) : day;
  }
  const d = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? day : `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Today (YYYY-MM-DD) in Lusaka. */
export function lusakaToday(now: Date = new Date()): string {
  return isoDayFmt.format(now);
}

/** The Lusaka day (YYYY-MM-DD) of an RFC 3339 timestamp, or null when it is not one. */
export function lusakaDayOf(iso: string): string | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : lusakaToday(d);
}

/** Add whole days to a YYYY-MM-DD date. */
export function addDays(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** First 8 characters of a UUID, for compact display of party ids the API does not name. */
export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : "—";
}

const wallPartsFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: LUSAKA_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/** Minutes Lusaka wall-clock time is ahead of UTC at `at` (from the tz database, not hard-coded). */
function lusakaOffsetMinutes(at: Date): number {
  const parts = Object.fromEntries(wallPartsFmt.formatToParts(at).map((p) => [p.type, p.value]));
  const wall = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return Math.round((wall - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}

/**
 * A `datetime-local` input value ("2026-09-27T14:05"), read as Lusaka time, to an RFC 3339 UTC timestamp.
 * Returns null for anything else.
 */
export function lusakaLocalToUtc(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local.trim());
  if (!m) return null;
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  if (Number.isNaN(asUtc)) return null;
  const offset = lusakaOffsetMinutes(new Date(asUtc));
  return new Date(asUtc - offset * 60000).toISOString().replace(/\.000Z$/, "Z");
}

/** "3 h 20 min", "2 d 4 h", "45 min" for a positive number of milliseconds. */
export function formatSpan(ms: number): string {
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const min = totalMin % 60;
  if (d > 0) return `${d} d ${h} h`;
  if (h > 0) return `${h} h ${min} min`;
  return `${min} min`;
}

export interface SlaState {
  /** Text with its meaning in words (never colour alone): "5 h 0 min left", "Overdue by 2 h 0 min". */
  text: string;
  overdue: boolean;
}

/** Time to or past an SLA deadline, relative to `now`. */
export function slaCountdown(dueAt: string | null | undefined, now: Date): SlaState | null {
  if (!dueAt) return null;
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return null;
  const diff = due - now.getTime();
  return diff >= 0 ? { text: `${formatSpan(diff)} left`, overdue: false } : { text: `Overdue by ${formatSpan(-diff)}`, overdue: true };
}

/** The Lusaka calendar month (YYYY-MM) of `now`. */
export function lusakaMonth(now: Date = new Date()): string {
  return lusakaToday(now).slice(0, 7);
}

/** YYYY-MM plus `n` months. */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map((x) => Number.parseInt(x, 10));
  const total = y * 12 + (m - 1) + n;
  const yy = Math.floor(total / 12);
  return `${String(yy).padStart(4, "0")}-${String(total - yy * 12 + 1).padStart(2, "0")}`;
}

const monthFmt = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", month: "long", year: "numeric" });

/** "September 2026" from "2026-09". */
export function formatMonth(month: string | null | undefined): string {
  if (!month || !/^\d{4}-\d{2}$/.test(month)) return month ?? "—";
  return monthFmt.format(new Date(`${month}-01T00:00:00Z`));
}

/** Whole days from `now` (Lusaka day) to a date or timestamp; negative when past. */
export function daysUntil(dayOrIso: string | null | undefined, now: Date): number | null {
  if (!dayOrIso) return null;
  const day = dayOrIso.length > 10 ? lusakaDayOf(dayOrIso) : dayOrIso;
  if (!day) return null;
  const a = Date.parse(`${lusakaToday(now)}T00:00:00Z`);
  const b = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

/** "4 min 05 s", "1 h 02 min", "12 s" for a number of seconds (response times, live clocks). */
export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h} h ${String(m).padStart(2, "0")} min`;
  if (m > 0) return `${m} min ${String(sec).padStart(2, "0")} s`;
  return `${sec} s`;
}

/** Whole seconds from `fromIso` to `to` (never negative), or null when the timestamp is not one. */
export function secondsBetween(fromIso: string | null | undefined, to: Date | string | null | undefined): number | null {
  if (!fromIso || !to) return null;
  const a = Date.parse(fromIso);
  const b = typeof to === "string" ? Date.parse(to) : to.getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.max(0, Math.floor((b - a) / 1000));
}

/** "14:05:09" Lusaka wall-clock time of an instant (the console's clocks). */
export function formatTimeOfDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : new Intl.DateTimeFormat("en-GB", { timeZone: LUSAKA_TZ, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(d);
}

/** A Lusaka `datetime-local` value ("2026-09-28T10:05") for an instant, to prefill inputs. */
export function lusakaLocalInput(at: Date): string {
  const parts = Object.fromEntries(wallPartsFmt.formatToParts(at).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${String(Number(parts.hour) % 24).padStart(2, "0")}:${parts.minute}`;
}
